import Konva from "konva";
import { Group, Rect, Text } from "react-konva";
import BaseElementComponent from "elements/base/base-element-component";
import consts from "shared/consts";
import { getTextColorForBackgroundColor } from "utils/color-utils";
import { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { EditableText } from "frontend/editableText";
import { useEvent } from "react-use";
import { useDebounce } from "frontend/hooks/debounce";
import { setMouseColResize, setMouseDrag, setMouseText, unsetMouse } from "utils/mouse-cursor-utils";
import { differenceInDays, startOfDay } from "date-fns";
import { GanttOpenTaskCard } from "elements/gantt/icons/gantt-open-task-card";
import ganttConstant from "elements/gantt/constants";
import { GanttTaskCell } from "elements/gantt/layout-builder";
import useObservableController from "elements/hooks/use-observable-controller";
import { useAtomValue } from "jotai";
import { selectedElementIdsAtom, transformerRefAtom } from "state-atoms";
import { IGanttBaseCellController } from "elements/gantt/controllers/base-cell-controller";
import { SupportedGanttElements } from "elements/gantt/types";
import { cleanDate } from "shared/util/date-utils";
import { GanttMondayIcon } from "elements/gantt/icons/gantt-monday-icon";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { getMondayItemId, isMondayGanttElement } from "elements/gantt/utils";
import { TaskErrorState } from "elements/gantt/components/task-error-state";

const START_CORNER_RADIUS = [0, 5, 5, 0];
const END_CORNER_RADIUS = [5, 0, 0, 5];
const PADDING = ganttConstant.taskCellPadding;

export default forwardRef(function GanttTaskComponent(
  {
    controller,
    onDrag,
    onDragStart,
    onDragEnd,
    setMouseDragFunc,
    onResize,
    onHover,
    ghostConnector,
  }: {
    onHover: (id: string | null) => void;
    controller: IGanttBaseCellController<SupportedGanttElements>;
    onDrag: (id: string, e: Konva.KonvaEventObject<DragEvent>, point: { x: number; y: number }) => void;
    onDragStart: (id: string) => void;
    onDragEnd: (id: string) => void;
    setMouseDragFunc: (e: Konva.KonvaEventObject<MouseEvent>) => void;
    onResize: ({
      id,
      x,
      y,
      side,
    }: {
      id: string;
      x: number;
      y: number;
      side: "start" | "end";
    }) => (e: Konva.KonvaEventObject<DragEvent>) => void;
    ghostConnector: {
      from: { taskId?: string; x: number; y: number };
      to: { taskId?: string; x: number; y: number };
    } | null;
  },
  ref: ForwardedRef<any>
) {
  const [isHovering, setIsHovering] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const transformerRef = useAtomValue(transformerRefAtom);
  const { updateItem, fetchItems } = useBoardTasks(controller.context.documentId, controller.context.pusherChannel);
  const selectedElementIds = useAtomValue(selectedElementIdsAtom);
  const connectorRef = useRef<any>(null);
  const taskCellRef = useRef<Konva.Rect>(null);
  const taskError = controller.getError?.();
  const hasError = !!taskError;
  const debounceHover = useDebounce(isHovering, 300);

  const title = controller.getTitle();
  const [currentTitle, setCurrentTitle] = useState(title ?? "");
  const debounceCurrentTitle = useDebounce(currentTitle, 500);

  const layout = controller.getLayout();
  const textRef = useRef<Konva.Text>(null);
  const isMondayItem = isMondayGanttElement(controller.element);

  useEffect(() => {
    if (title !== currentTitle) {
      if (isMondayGanttElement(controller.element)) {
        updateItem(getMondayItemId(controller.element), controller.element.integrationId, {
          name: debounceCurrentTitle,
        });
      }
      controller.changeTitle(debounceCurrentTitle);
    }
  }, [debounceCurrentTitle]);

  useObservableController(controller, undefined, () => {
    if (controller.isSelected()) {
      transformerRef?.current?.forceUpdate();
    }
  });

  const handleRetry = useCallback(() => {
    if (isMondayGanttElement(controller.element)) {
      const itemId = (controller.element as any).configuration?.itemId || (controller.element as any).mondayItemId;
      if (!itemId) {
        return;
      }
      fetchItems({
        [controller.element.integrationId]: {
          itemIds: [itemId],
        },
      });
    }
  }, [controller.element, fetchItems]);

  const unsetMouseAction = useCallback((e?: any) => {
    const stage = Konva.stages[0];
    if (stage) {
      unsetMouse(e, stage);
    }
  }, []);

  useEvent("keydown", (e) => {
    if (e.key === "Escape") {
      unsetMouseAction(e);
    }
  });

  useEffect(() => {
    setCurrentTitle(title);
  }, [title]);

  useEffect(() => {
    if (selectedElementIds.length === 0) {
      setIsEditing(false);
    }
  }, [selectedElementIds]);

  useEffect(() => {
    if (!ghostConnector && connectorRef.current) {
      setIsEditing(false);
      setIsHovering(false);
    }

    connectorRef.current = ghostConnector;
  }, [ghostConnector]);

  useImperativeHandle(
    ref,
    () => {
      return {
        setIsHovering: (isHovering: boolean) => {
          setIsHovering(isHovering);
        },
        id: controller.id,
        taskCellRef,
      };
    },
    [controller.id, onHover]
  );

  useEffect(() => {
    onHover(isHovering ? controller.id : null);
  }, [isHovering]);

  if (!layout) {
    return null;
  }

  const background = controller.getBackgroundColor();
  const textColor = getTextColorForBackgroundColor(background);

  const startShift = 0;
  const endShift = 0;

  const cornerRadius = calculateCornerRadius(endShift, startShift);

  if (layout.width - PADDING * 2 < 0) {
    return null;
  }
  let width = Math.max(0, layout.width - PADDING * 2);

  const height = layout.height - PADDING * 2;

  const endAmountDiff = getDaysDiff(controller.getEndDate() || cleanDate(new Date()), controller.getGanttEndDate());
  const startAmountDiff = getDaysDiff(
    controller.getGanttStartDate(),
    controller.getStartDate() || cleanDate(new Date())
  );

  width += Math.abs(startShift);
  width += Math.abs(endShift);

  const lineWidth = 5;

  const isOutSide = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!taskCellRef.current) {
      return false;
    }
    const rect = taskCellRef.current.getClientRect();
    const pos = e.target.getStage()?.getPointerPosition();
    if (!pos) {
      return false;
    }
    return pos.x < rect.x || pos.x > rect.x + rect.width || pos.y < rect.y || pos.y > rect.y + rect.height;
  };
  const errorLayoutSize = width <= 100 ? "small" : "large";

  return (
    <Group
      onMouseLeave={(e) => {
        if (isOutSide(e)) {
          setIsHovering(false);
          unsetMouseAction(e);
        }
      }}
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onDblClick={() => {
        setIsEditing(true);
      }}
      onMouseMove={(e) => {
        if (isEditing) {
          setMouseText(e);
        }
      }}
    >
      <BaseElementComponent<SupportedGanttElements>
        x={layout.x + PADDING + startShift}
        y={layout.y + PADDING}
        id={controller.id}
        element={{ ...controller.element, scaleX: 1, scaleY: 1 }}
        controller={controller}
        isConnectable={false}
        isSelectable={true}
        isDraggable={false}
        type={controller.element.type}
      >
        <Rect
          id={"anchor-drag-task"}
          x={-PADDING}
          y={-PADDING / 2}
          width={layout.width}
          height={layout.height - PADDING}
          hitStrokeWidth={10}
          cornerRadius={[5, 5, 5, 5]}
          fill="transparent"
          ref={taskCellRef}
        />
        <Rect
          id={`task-${controller.id}`}
          width={width}
          height={height}
          fill={ganttConstant.TasksColors[background] ? ganttConstant.TasksColors[background][0] : background}
          cornerRadius={cornerRadius}
        />
        {!hasError && (
          <Rect width={width} height={height} fill={"#FFFFFF80"} cornerRadius={cornerRadius} listening={false} />
        )}

        <Rect
          id={`task-${controller.id}`}
          width={8}
          height={height}
          fill={hasError ? "#DEE1EB" : background}
          cornerRadius={[5, 0, 0, 5]}
        />
        {controller.isSelected() && (
          <Rect
            listening={false}
            width={width}
            height={height}
            fill={"transparent"}
            stroke="#00A1FF"
            cornerRadius={[5, 5, 5, 5]}
          />
        )}

        <EditableText
          x={PADDING + 8}
          y={PADDING - 5}
          width={width - PADDING * 4}
          text={title}
          fill={textColor}
          height={height - PADDING - (layout.isCutStart || layout.isCutEnd ? 15 : 0)}
          fontSize={15}
          isEditing={isEditing}
          isFixedHeight={true}
          onChange={(newTextValue) => {
            setCurrentTitle(newTextValue);
          }}
          placeholder=""
          font={consts.DEFAULTS.FONT}
          onStopEditing={() => {
            unsetMouseAction();
          }}
          ellipsis={true}
        />

        {!hasError && isMondayItem && <GanttMondayIcon x={width - PADDING * 3 - 2} y={height - PADDING * 2 - 2} />}

        {layout.isCutStart && startAmountDiff > 0 && (
          <Text
            id={`task-${controller.id}-startText`}
            x={PADDING}
            y={height - 15}
            width={width - PADDING * 2}
            text={`Started ${startAmountDiff} day${startAmountDiff > 1 ? "s" : ""} ago...`}
            fontFamily={"Poppins"}
            fill={textColor}
            height={20}
            ellipsis={true}
            ref={textRef}
            align="left"
            opacity={0.5}
          />
        )}
        {layout.isCutEnd && endAmountDiff > 0 && (
          <Text
            id={`task-${controller.id}-endText`}
            x={PADDING + 8}
            y={height - 15}
            width={width - PADDING * 2 - (isMondayItem ? 10 : 0)}
            text={`${endAmountDiff} day${endAmountDiff > 1 ? "s" : ""} more...`}
            fontFamily={"Poppins"}
            fill={textColor}
            height={20}
            ellipsis={true}
            ref={textRef}
            align="left"
            opacity={0.5}
          />
        )}

        {!hasError && (
          <Group x={width - 15} y={5} id={`task-${controller.id}-openTask`}>
            <GanttOpenTaskCard id={`task-${controller.id}-openTask`} />
          </Group>
        )}

        {isHovering && !hasError && (
          <>
            <Rect
              id={"anchor-drag-task"}
              x={0}
              y={0}
              width={layout.width - PADDING * 2}
              height={layout.height - PADDING * 2}
              fill={"transparent"}
              stroke="#00A1FF"
              cornerRadius={[5, 5, 5, 5]}
              onMouseEnter={setMouseDrag}
              onMouseMove={setMouseDrag}
              hitStrokeWidth={10}
            />
            <Rect
              id={"anchor-drag-task"}
              x={-PADDING}
              y={-PADDING}
              width={layout.width}
              height={layout.height}
              onMouseEnter={(e) => {
                setMouseDrag(e);
                setMouseDragFunc(e);
              }}
              onMouseMove={setMouseDrag}
              fill={"transparent"}
              stroke={"transparent"}
              hitStrokeWidth={10}
              draggable
              onDragMove={(e) => {
                const stage = Konva.stages[0];

                const transform = stage.getAbsoluteTransform().copy();
                transform.invert();
                const pos = stage.getPointerPosition();
                if (!pos) {
                  return;
                }
                const actualPoint = pos ? transform.point(pos) : { x: 0, y: 0 };
                const ganttBounds = controller.getGanttLayoutRect();
                const taskCellBounds = controller.getLayout();
                if (!taskCellBounds) {
                  return;
                }
                const internalPoint = {
                  x: actualPoint.x - ganttBounds.x,
                  y: actualPoint.y - ganttBounds.y,
                };

                onDrag(controller.id, e, internalPoint);
              }}
              onDragStart={() => {
                onDragStart(controller.id);
              }}
              onDragEnd={(e) => {
                onDragEnd(controller.id);
                e.currentTarget.position({ x: -PADDING, y: -PADDING });
              }}
              cornerRadius={[5, 5, 5, 5]}
            />
          </>
        )}

        {(hasError || !controller.context.isLoggedIn) && (
          <TaskErrorState
            layout={errorLayoutSize}
            errorType={taskError ?? "failed-loading"}
            open={debounceHover}
            _onAction={() => {
              handleRetry();
            }}
          />
        )}

        <Rect
          id={"anchor-resize-right"}
          listening={!hasError}
          x={layout.width - lineWidth - PADDING * 2}
          y={0}
          width={lineWidth + 4}
          height={layout.height - PADDING * 2}
          fill={"transparent"}
          cornerRadius={[0, 5, 5, 0]}
          draggable
          onDragMove={(e) => {
            const y = 0;
            const x = layout.width - lineWidth - PADDING * 2;
            setMouseColResize(e);
            onResize({ id: controller.id, x: x, y, side: "end" })(e);
          }}
          onDragStart={(e) => {
            setMouseColResize(e);
          }}
          onMouseMove={(e) => {
            setMouseColResize(e);
          }}
          onMouseEnter={setMouseColResize}
          onMouseLeave={(e) => {
            unsetMouse(e);
          }}
        />
        <Rect
          id={"anchor-resize-left"}
          listening={!hasError}
          x={0}
          y={0}
          width={lineWidth + 4}
          height={layout.height - PADDING * 2}
          fill={"transparent"}
          cornerRadius={[5, 0, 0, 5]}
          draggable
          onDragMove={(e) => {
            const y = 0;
            const x = 0;
            setMouseColResize(e);
            onResize({ id: controller.id, x: x, y, side: "start" })(e);
          }}
          onDragStart={(e) => {
            setMouseColResize(e);
          }}
          onMouseMove={(e) => {
            setMouseColResize(e);
          }}
          onMouseEnter={setMouseColResize}
          onMouseLeave={(e) => {
            unsetMouse(e);
          }}
        />
      </BaseElementComponent>
    </Group>
  );
});

const getDaysDiff = (date1?: number | Date, date2?: number | Date) => {
  return Math.abs(differenceInDays(startOfDay(date1 ?? 0), startOfDay(date2 ?? 0)));
};

const calculateCornerRadius = (endShift: number, startShift: number) => {
  let cornerRadius: number | number[] = 5;
  if (endShift !== 0) {
    cornerRadius = END_CORNER_RADIUS;
  }
  if (startShift !== 0) {
    cornerRadius = START_CORNER_RADIUS;
  }

  if (!Array.isArray(cornerRadius) && cornerRadius < 0) {
    return 0;
  }

  return cornerRadius;
};

export const measureTextTitle = ({
  title = "",
  layout,
  isEditing,
}: {
  title: string;
  layout: GanttTaskCell;
  isEditing: boolean;
}) => {
  const width = layout.width - PADDING * 4 - 28;

  let height = layout.height - PADDING * 2;

  const amountOfRowsThatFit = Math.floor(height / ganttConstant.TaskTitleRowHeight);

  const ellipsedText = title
    .split("\n")
    .slice(0, amountOfRowsThatFit - 1)
    .concat("...")
    .map((line) => line.trim())
    .join("\n");

  const linesCount = title.split("\n").length;
  let newText = amountOfRowsThatFit >= linesCount ? title : ellipsedText;
  const textBlockHeight = Math.min(linesCount, amountOfRowsThatFit) * ganttConstant.TaskTitleRowHeight;

  height = !isEditing ? textBlockHeight : height - 8;
  const shrunkTitle = newText.slice(0, Math.floor(width / 15));
  newText = newText.length > shrunkTitle.length ? shrunkTitle.trim() + "..." : newText;

  return { width, height, newTitle: isEditing ? title : newText.trim() };
};
